import apiClient from './apiClient';

/**
 * EVM虚拟机管理服务 (Admin Version)
 *
 * 处理管理员级别的虚拟机管理操作
 * 管理员可以管理所有用户的VM资源
 *
 * 所有路由使用 /api/evm/admin/* 前缀
 */
// Area rows from /api/evm/admin/area carry only id + name; the ISO country lives in evm_area.remark
// (served by /api/admin/evm/areas). Attach it as `country` so pickers can show a flag.
let areaCountryPromise = null;
const loadAreaCountries = () => {
  if (!areaCountryPromise) {
    areaCountryPromise = apiClient.get('/api/admin/evm/areas')
      .then((r) => Object.fromEntries((r.data?.data?.areas || []).map((a) => [Number(a.id), String(a.remark || '').trim().toUpperCase()])))
      .catch(() => { areaCountryPromise = null; return {}; });
  }
  return areaCountryPromise;
};
const attachAreaCountries = async (payload) => {
  const list = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload?.data?.data) ? payload.data.data : null);
  if (!list || !list.length) return;
  const map = await loadAreaCountries();
  list.forEach((a) => { if (a && a.country === undefined && map[Number(a.id)]) a.country = map[Number(a.id)]; });
};

class EvmAdminService {
  // ==========================================
  // VM列表和创建
  // ==========================================

  /**
   * 获取VM列表
   * @param {Object} params - 查询参数
   */
  async getVms(params = {}) {
    try {
      const response = await apiClient.get('/api/evm/admin/vms', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取VM详情
   * @param {number} id - VM ID
   */
  async getVmDetail(id) {
    try {
      const response = await apiClient.get(`/api/evm/admin/vms/${id}`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取VM磁盘列表
   * @param {number} id - VM ID
   */
  async getVmDisks(id) {
    try {
      const response = await apiClient.get(`/api/evm/admin/vms/${id}/disks`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建VM
   * @param {Object} data - VM创建数据
   */
  async createVm(data) {
    try {
      const response = await apiClient.post('/api/evm/admin/vms', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 管理员专用操作
  // ==========================================

  /**
   * 暂停VM (管理员)
   * @param {number} id - VM ID
   */
  async suspendVm(id) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/suspend`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 恢复VM (管理员)
   * @param {number} id - VM ID
   */
  async unsuspendVm(id) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/unsuspend`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除VM
   * @param {number} id - VM ID
   */
  async deleteVm(id) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/delete`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 迁移VM
   * @param {number} id - VM ID
   * @param {number} node_id - 目标节点ID
   */
  async migrateVm(id, node_id) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/migrate`, { node_id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 重置流量
   * @param {number} id - VM ID
   */
  async resetFlow(id) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/resetflow`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 电源管理 (复用用户端点)
  // ==========================================

  /**
   * 电源操作
   * @param {number} id - VM ID
   * @param {string} action - boot|shutdown|reboot|reset|destroy|suspend|resume
   */
  async power(id, action) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/power`, { base: action });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取VM状态
   */
  async getState(id) {
    try {
      const response = await apiClient.get(`/api/evm/admin/vms/${id}/state`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取NoVNC连接
   */
  async getNoVnc(id) {
    try {
      const response = await apiClient.get(`/api/evm/admin/vms/${id}/novnc`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取 Terminal WebSocket 连接信息
   */
  async getTerminal(id) {
    try {
      const response = await apiClient.get(`/api/evm/admin/vms/${id}/terminal`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 配置管理 (复用用户端点)
  // ==========================================

  /**
   * 设置主机名
   */
  async setHostname(id, hostname) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/hostname`, { hostname });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 设置密码
   */
  async setPassword(id, passwd) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/password`, { passwd });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 设置VNC密码
   */
  async setVncPassword(id, passwd) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/vnc_password`, { passwd });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 重建VM
   * @param {number} id - VM ID
   * @param {Object} data - { image_id, passwd? }
   */
  async rebuild(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/rebuild`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 更新VM配置 (CPU、内存、带宽、流量)
   * @param {number} id - VM ID
   * @param {Object} data - { core, memory, net_out, flow_limit }
   */
  async updateConfig(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/config`, data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 磁盘管理
  // ==========================================

  /**
   * 添加磁盘
   */
  async addDisk(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/disk/add`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 更新磁盘
   */
  async updateDisk(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/disk/update`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除磁盘
   */
  async deleteDisk(id, disk_id) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/disk/delete`, { disk_id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 快照管理
  // ==========================================

  /**
   * 创建快照
   */
  async createSnapshot(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/snapshot`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 恢复快照
   */
  async recoverSnapshot(id, snapshot_name) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/snapshot/recover`, { snapshot_name });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除快照
   */
  async deleteSnapshot(id, snapshot_name) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/snapshot/delete`, { snapshot_name });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 备份管理
  // ==========================================

  /**
   * 获取备份列表
   * @param {number} vmId - VM ID
   */
  async getBackups(vmId) {
    try {
      const response = await apiClient.get(`/api/evm/admin/backup`, { params: { vm: vmId } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建备份
   * @param {number} id - VM ID
   */
  async createBackup(id) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/backup`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // ISO挂载
  // ==========================================

  /**
   * 挂载ISO
   */
  async mountIso(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/mount`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // IP管理
  // ==========================================

  /**
   * 获取VM可绑定的IP地址
   */
  async getAvailableIps(id) {
    try {
      const response = await apiClient.get(`/api/evm/admin/vms/${id}/ip/available`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 绑定IP
   */
  async bindIp(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/ip/bind`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 解绑IP
   */
  async unbindIp(id, ip_id) {
    try {
      const response = await apiClient.post(`/api/evm/admin/vms/${id}/ip/unbind`, { ip_id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 辅助资源管理
  // ==========================================

  /**
   * 获取区域列表
   */
  async getAreas() {
    try {
      const response = await apiClient.get('/api/evm/admin/area');
      await attachAreaCountries(response.data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取节点列表
   */
  async getNodes(areaId) {
    try {
      const response = await apiClient.get('/api/evm/admin/nodes', {
        params: areaId ? { area_id: areaId } : {},
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取镜像列表
   */
  async getImages() {
    try {
      const response = await apiClient.get('/api/evm/admin/images');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取用户列表
   */
  async getUsers() {
    try {
      const response = await apiClient.get('/api/evm/admin/users');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取磁盘配额列表
   */
  async getDisks() {
    try {
      const response = await apiClient.get('/api/evm/admin/disk');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取IP池列表
   */
  async getIpPools() {
    try {
      const response = await apiClient.get('/api/evm/admin/ip_pool');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 模板和产品管理
  // ==========================================

  /**
   * 获取模板列表
   */
  async getTemplates() {
    try {
      const response = await apiClient.get('/api/evm/admin/templates');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取产品列表
   */
  async getProducts() {
    try {
      const response = await apiClient.get('/api/evm/admin/products');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 节点管理
  // ==========================================

  /**
   * 获取节点列表 (详细)
   */
  async getNodeList(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/evm/node', { params });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取节点详情
   */
  async getNode(id) {
    try {
      const response = await apiClient.get(`/api/admin/evm/node/${id}`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建节点
   */
  async createNode(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/node', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 更新节点
   */
  async updateNode(id, data) {
    try {
      const response = await apiClient.post(`/api/admin/evm/node/${id}`, data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除节点
   */
  async deleteNode(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/node/${id}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 测试节点连接
   */
  async testNodeConnection(nodeId) {
    try {
      const response = await apiClient.post('/api/admin/evm/node/test', { node_id: nodeId });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 检测节点环境
   */
  async checkNode(nodeId) {
    try {
      const response = await apiClient.post('/api/admin/evm/node/check', { node_id: nodeId }, { timeout: 100000 });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 部署节点
   */
  async deployNode(nodeId, options = {}) {
    try {
      const response = await apiClient.post('/api/admin/evm/node/deploy', { ...options, node_id: nodeId });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message, data: error.response?.data?.data };
    }
  }

  /** 部署任务(offset: 已读日志字节数,增量拉取) */
  async getDeployJob(jobId, offset = 0) {
    try {
      const response = await apiClient.get(`/api/admin/evm/node/deploy-job/${jobId}`, { params: { offset } });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  async getLatestDeployJob(nodeId, offset = 0) {
    try {
      const response = await apiClient.get(`/api/admin/evm/node/${nodeId}/deploy-job`, { params: { offset } });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取节点状态
   */
  async getNodeStatus(nodeId) {
    try {
      const response = await apiClient.post('/api/admin/evm/node/status', { node_id: nodeId });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 重新生成节点凭证
   */
  async regenerateNodeCredentials(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/node/${id}/credentials`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 可用区 (Zone) 管理
  // ==========================================

  /**
   * 获取可用区列表
   */
  async getZoneList(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/evm/zone', { params });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 按地区获取可用区列表
   */
  async getZonesByArea(areaId) {
    try {
      const response = await apiClient.get('/api/admin/evm/zone/by-area', { params: { area_id: areaId } });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取可用区详情
   */
  async getZone(id) {
    try {
      const response = await apiClient.get(`/api/admin/evm/zone/${id}`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建可用区
   */
  async createZone(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/zone', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 更新可用区
   */
  async updateZone(id, data) {
    try {
      const response = await apiClient.post(`/api/admin/evm/zone/${id}`, data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /** Region names with their translations (admin language tabs). */
  async getAreaNames() {
    try {
      const response = await apiClient.get('/api/admin/evm/areas');
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  async updateAreaName(id, data) {
    try {
      const response = await apiClient.post(`/api/admin/evm/areas/${id}`, data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除可用区
   */
  async deleteZone(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/zone/${id}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取可用区内的节点
   */
  async getZoneAvailableNodes(id) {
    try {
      const response = await apiClient.get(`/api/admin/evm/zone/${id}/nodes`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // VPC 管理
  // ==========================================

  /**
   * 获取VPC列表
   */
  async getVpcList(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/evm/vpc', { params });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取VPC详情
   */
  async getVpc(id) {
    try {
      const response = await apiClient.get(`/api/admin/evm/vpc/${id}`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建VPC
   */
  async createVpc(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/vpc', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 更新VPC
   */
  async updateVpc(id, data) {
    try {
      const response = await apiClient.post(`/api/admin/evm/vpc/${id}`, data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除VPC
   */
  async deleteVpc(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/vpc/${id}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 添加VPC子网
   */
  async addVpcSubnet(vpcId, data) {
    try {
      const response = await apiClient.post(`/api/admin/evm/vpc/${vpcId}/subnet`, data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除VPC子网
   */
  async deleteVpcSubnet(vpcId, subnetId) {
    try {
      const response = await apiClient.post(`/api/admin/evm/vpc/${vpcId}/subnet/${subnetId}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取VPC节点列表
   */
  async getVpcNodes(vpcId) {
    try {
      const response = await apiClient.get(`/api/admin/evm/vpc/${vpcId}/nodes`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 添加VPC节点
   */
  async addVpcNode(vpcId, nodeId) {
    try {
      const response = await apiClient.post(`/api/admin/evm/vpc/${vpcId}/node`, { node_id: nodeId });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 移除VPC节点
   */
  async removeVpcNode(vpcId, nodeId) {
    try {
      const response = await apiClient.post(`/api/admin/evm/vpc/${vpcId}/node/${nodeId}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // IP Pool 管理
  // ==========================================

  /**
   * 获取IP Pool列表
   */
  async getIpPoolList(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/evm/ip-pool', { params });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取IP Pool详情
   */
  async getIpPool(id) {
    try {
      const response = await apiClient.get(`/api/admin/evm/ip-pool/${id}`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建IPv4 Pool
   */
  async createIpPoolV4(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/ip-pool/create-v4', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建IPv6 Pool
   */
  async createIpPoolV6(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/ip-pool/create-v6', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 添加IP地址到Pool
   */
  async addIpAddresses(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/ip-pool/add-addresses', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 添加IP地址范围到Pool
   */
  async addIpAddressRange(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/ip-pool/add-address-range', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 预分配IPv6地址 (从v6池批量预留地址)
   * @param {object} data - { pool_id, count }
   */
  async preallocateIpV6(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/ip-pool/preallocate-v6', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 更新IP Pool
   */
  async updateIpPool(id, data) {
    try {
      const response = await apiClient.post(`/api/admin/evm/ip-pool/${id}`, data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除IP Pool
   */
  async deleteIpPool(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/ip-pool/${id}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取Pool中的IP地址列表
   */
  async getIpPoolAddresses(poolId) {
    try {
      const response = await apiClient.get(`/api/admin/evm/ip-pool/${poolId}/addresses`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  async updateIpAddressMac(id, mac) {
    try {
      const response = await apiClient.post(`/api/admin/evm/ip-pool/address/${id}/mac`, { mac });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  // ---- IPv6 前缀分配 (allocator pools) ----
  async _v6(method, url, data) {
    try {
      const response = method === 'get'
        ? await apiClient.get(url, { params: data })
        : await apiClient.post(url, data || {});
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /** 节点 IPv6 上联 {config, report, reported_at, task, suggested, physical_nic} */
  getNodeIpv6Uplink(nodeId) { return this._v6('get', `/api/admin/evm/node/${nodeId}/ipv6-uplink`); }
  /** data: {mode, iface, vlan, address, gateway, prefixes[], apply, require_external} */
  saveNodeIpv6Uplink(nodeId, data) { return this._v6('post', `/api/admin/evm/node/${nodeId}/ipv6-uplink`, data); }

  /** 前缀池详情 {pool, supernet, stats, allocations} */
  getIpPoolV6(poolId, params = {}) { return this._v6('get', `/api/admin/evm/ip-pool/${poolId}/v6`, params); }
  allocateIpPoolV6(poolId, data) { return this._v6('post', `/api/admin/evm/ip-pool/${poolId}/v6/allocate`, data); }
  releaseIpv6Prefix(allocId, cooldownDays) {
    const body = cooldownDays === undefined || cooldownDays === null || cooldownDays === '' ? {} : { cooldown_days: cooldownDays };
    return this._v6('post', `/api/admin/evm/ip-pool/v6/${allocId}/release`, body);
  }
  reuseIpv6Prefix(allocId) { return this._v6('post', `/api/admin/evm/ip-pool/v6/${allocId}/reuse`); }
  attachIpv6Prefix(allocId, vmId) { return this._v6('post', `/api/admin/evm/ip-pool/v6/${allocId}/attach`, { vm_id: vmId || 0 }); }

  // ==========================================
  // EIP 管理
  // ==========================================

  /**
   * 获取EIP列表
   */
  async getEipList(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/evm/eip', { params });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 分配IPv4 EIP
   */
  async allocateEipV4(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/eip/allocate', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 批量分配IPv4 EIP
   */
  async batchAllocateEipV4(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/eip/batch-allocate', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 分配IPv6 EIP
   */
  async allocateEipV6(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/eip/allocate-v6', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 批量分配IPv6 EIP
   */
  async batchAllocateEipV6(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/eip/batch-allocate-v6', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 释放EIP
   */
  async releaseEip(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/eip/${id}/release`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 绑定EIP到VM
   */
  async bindEip(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/eip/bind', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 绑定IPv6 EIP到VM
   * @param {object} data - { eip_id, vm_id, bridge? }
   */
  async bindEipV6(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/eip/bind-v6', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 分配IPv6地址段 (为用户分配一个v6子网块)
   * @param {object} data - { user_id, prefix, prefix_len, node_id?, bridge? }
   */
  async allocateEipV6Block(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/eip/allocate-v6-block', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 解绑EIP
   */
  async unbindEip(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/eip/${id}/unbind`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 安全组管理
  // ==========================================

  /**
   * 获取安全组列表
   */
  async getSecurityGroupList(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/evm/security-group', { params });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取安全组详情
   */
  async getSecurityGroup(id) {
    try {
      const response = await apiClient.get(`/api/admin/evm/security-group/${id}`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建安全组
   */
  async createSecurityGroup(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/security-group', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 更新安全组
   */
  async updateSecurityGroup(id, data) {
    try {
      const response = await apiClient.post(`/api/admin/evm/security-group/${id}`, data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除安全组
   */
  async deleteSecurityGroup(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/security-group/${id}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 添加安全组规则
   */
  async addSecurityGroupRule(groupId, data) {
    try {
      const response = await apiClient.post(`/api/admin/evm/security-group/${groupId}/rule`, data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除安全组规则
   */
  async deleteSecurityGroupRule(groupId, ruleId) {
    try {
      const response = await apiClient.post(`/api/admin/evm/security-group/${groupId}/rule/${ruleId}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 镜像管理 (完整 CRUD)
  // ==========================================

  /**
   * 获取镜像列表
   */
  async getImageList(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/evm/image', { params });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取镜像类型列表
   */
  async getImageTypes() {
    try {
      const response = await apiClient.get('/api/admin/evm/image/types');
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取镜像详情
   */
  async getImage(id) {
    try {
      const response = await apiClient.get(`/api/admin/evm/image/${id}`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建镜像
   */
  async createImage(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/image', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 更新镜像
   */
  async updateImage(id, data) {
    try {
      const response = await apiClient.post(`/api/admin/evm/image/${id}`, data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除镜像
   */
  async deleteImage(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/image/${id}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 切换镜像状态
   */
  async toggleImageStatus(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/image/${id}/toggle-status`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 镜像仓库 (同步/发布/导入/上传/任务)
  // ==========================================

  async _imgGet(path, params) {
    try {
      const response = await apiClient.get(path, { params });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  async _imgPost(path, data, config) {
    try {
      const response = await apiClient.post(path, data, config);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  getImageNodes(id) { return this._imgGet(`/api/admin/evm/image/${id}/nodes`); }
  syncImage(id, nodeIds) { return this._imgPost(`/api/admin/evm/image/${id}/sync`, nodeIds ? { node_ids: nodeIds } : {}); }
  removeImageFromNode(id, nodeId) { return this._imgPost(`/api/admin/evm/image/${id}/node/${nodeId}/remove`, {}); }
  publishImage(id, published) { return this._imgPost(`/api/admin/evm/image/${id}/publish`, { published: published ? 1 : 0 }); }
  retireImage(id, retired) { return this._imgPost(`/api/admin/evm/image/${id}/retire`, { retired: retired ? 1 : 0 }); }
  getImageDownloadLink(id) { return this._imgPost(`/api/admin/evm/image/${id}/download-link`, {}); }
  getImageInbox() { return this._imgGet('/api/admin/evm/image-inbox'); }
  importImageFromInbox(data) { return this._imgPost('/api/admin/evm/image-inbox/import', data); }
  fetchImageFromUrl(data) { return this._imgPost('/api/admin/evm/image-fetch', data); }
  initImageUpload(data) { return this._imgPost('/api/admin/evm/image-upload/init', data); }
  getImageUpload(jobId) { return this._imgGet(`/api/admin/evm/image-upload/${jobId}`); }
  /** raw chunk upload; throws on network error so the caller can retry */
  async uploadImageChunk(jobId, offset, blob) {
    const response = await apiClient.post(`/api/admin/evm/image-upload/${jobId}/chunk`, blob, {
      params: { offset },
      headers: { 'Content-Type': 'application/octet-stream' },
      timeout: 300000,
      transformRequest: [(d) => d],
    });
    return response.data;
  }
  completeImageUpload(jobId) { return this._imgPost(`/api/admin/evm/image-upload/${jobId}/complete`, {}); }
  getImageJobs(params = {}) { return this._imgGet('/api/admin/evm/image-jobs', params); }
  getImageJob(id) { return this._imgGet(`/api/admin/evm/image-jobs/${id}`); }
  cancelImageJob(id) { return this._imgPost(`/api/admin/evm/image-jobs/${id}/cancel`, {}); }
  getImageSettings() { return this._imgGet('/api/admin/evm/image-settings'); }
  saveImageSettings(data) { return this._imgPost('/api/admin/evm/image-settings', data); }

  /** 导出 VM 系统盘为镜像（仅管理员） */
  exportVmImage(id, data) { return this._imgPost(`/api/evm/admin/vms/${id}/export-image`, data); }

  // ==========================================
  // 产品管理 (完整 CRUD)
  // ==========================================

  /**
   * 获取产品列表
   */
  async getProductList(params = {}) {
    try {
      const response = await apiClient.get('/api/evm/admin/products', { params });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取产品详情
   */
  async getProduct(id) {
    try {
      const response = await apiClient.get(`/api/evm/admin/products/${id}`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建产品
   */
  async createProduct(data) {
    try {
      const response = await apiClient.post('/api/evm/admin/products', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 更新产品
   */
  async updateProduct(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/admin/products/${id}`, data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除产品
   */
  async deleteProduct(id) {
    try {
      const response = await apiClient.post(`/api/evm/admin/products/${id}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取产品配置选项 (设备配置 + 附加购买配置)
   */
  async getProductConfigOptions() {
    try {
      const response = await apiClient.get('/api/evm/admin/products/config-options');
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取存储池列表 (可按节点过滤)
   */
  async getStoragePools(nodeIds = '') {
    try {
      const params = nodeIds ? { node_ids: nodeIds } : {};
      const response = await apiClient.get('/api/evm/admin/products/storage-pools', { params });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 配置模板管理 (完整 CRUD)
  // ==========================================

  /**
   * 获取模板列表
   */
  async getTemplateList(params = {}) {
    try {
      const response = await apiClient.get('/api/evm/admin/templates', { params });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取模板详情
   */
  async getTemplate(id) {
    try {
      const response = await apiClient.get(`/api/evm/admin/templates/${id}`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建模板
   */
  async createTemplate(data) {
    try {
      const response = await apiClient.post('/api/evm/admin/templates', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 更新模板
   */
  async updateTemplate(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/admin/templates/${id}`, data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除模板
   */
  async deleteTemplate(id) {
    try {
      const response = await apiClient.post(`/api/evm/admin/templates/${id}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取设备配置选项
   */
  async getTemplateDeviceOptions() {
    try {
      const response = await apiClient.get('/api/evm/admin/templates/device-options');
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 端口转发管理
  // ==========================================

  /**
   * 获取端口转发列表
   */
  async getPortForwardList(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/evm/port-forward', { params });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建端口转发
   */
  async createPortForward(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/port-forward', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除端口转发
   */
  async deletePortForward(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/port-forward/${id}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 强制删除端口转发
   */
  async forceDeletePortForward(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/port-forward/${id}/force-delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /** NAT websites (admin: no DNS proof required) */
  async createSiteForward(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/site-forward', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  async deleteSiteForward(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/site-forward/${id}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 启动脚本管理 (CloudInit)
  // ==========================================

  /**
   * 获取启动脚本列表
   */
  async getStartupScriptList(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/evm/startup-script', { params });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取可用脚本列表 (用于镜像选择)
   */
  async getAvailableStartupScripts(platform = 'linux') {
    try {
      const response = await apiClient.get('/api/admin/evm/startup-script/list', { params: { platform } });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取启动脚本详情
   */
  async getStartupScript(id) {
    try {
      const response = await apiClient.get(`/api/admin/evm/startup-script/${id}`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建启动脚本
   */
  async createStartupScript(data) {
    try {
      const response = await apiClient.post('/api/admin/evm/startup-script', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 更新启动脚本
   */
  async updateStartupScript(id, data) {
    try {
      const response = await apiClient.post(`/api/admin/evm/startup-script/${id}`, data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除启动脚本
   */
  async deleteStartupScript(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/startup-script/${id}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 切换启动脚本状态
   */
  async toggleStartupScriptStatus(id) {
    try {
      const response = await apiClient.post(`/api/admin/evm/startup-script/${id}/toggle-status`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取脚本平台类型列表
   */
  async getStartupScriptPlatforms() {
    try {
      const response = await apiClient.get('/api/admin/evm/startup-script/platforms');
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取脚本类型列表
   */
  async getStartupScriptTypes() {
    try {
      const response = await apiClient.get('/api/admin/evm/startup-script/types');
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  // ---- 2026-10 infra batch (network / metrics / backups) ----
  async _get(path, params) {
    try {
      const response = await apiClient.get(path, { params });
      return response.data;
    } catch (error) {
      return { success: false, status: 'error', error: error.response?.data?.msg || error.message };
    }
  }

  async _post(path, data) {
    try {
      const response = await apiClient.post(path, data);
      return response.data;
    } catch (error) {
      return { success: false, status: 'error', error: error.response?.data?.msg || error.message };
    }
  }

  getVmNetwork(id) { return this._get(`/api/evm/admin/vms/${id}/network`); }
  backfillIpv6(id) { return this._post(`/api/evm/admin/vms/${id}/ipv6/backfill`); }
  attachVpc(id, subnetId, restart = false, confirmCross = false) { return this._post(`/api/evm/admin/vms/${id}/vpc/attach`, { subnet_id: subnetId, restart, confirm_cross: confirmCross ? 1 : 0 }); }
  detachVpc(id, portId, restart = false) { return this._post(`/api/evm/admin/vms/${id}/vpc/detach`, { port_id: portId, restart }); }
  setVmSecurityGroups(id, ids) { return this._post(`/api/evm/admin/vms/${id}/security-groups`, { ids }); }
  getVmMetrics(id, range = '1h') { return this._get(`/api/evm/admin/vms/${id}/metrics`, { range }); }
  getVmTrafficSeries(id, granularity = 'hour') { return this._get(`/api/evm/admin/vms/${id}/traffic-series`, { granularity }); }
  getTrafficOverview(granularity = 'day', nodeId) { return this._get('/api/evm/admin/traffic', { granularity, node_id: nodeId }); }
  vmRescue(id, action) { return this._post(`/api/evm/admin/vms/${id}/rescue`, { action }); }
  getRdnsRequests(params) { return this._get('/api/evm/admin/rdns', params); }
  getRdnsZones() { return this._get('/api/evm/admin/rdns/zones'); }
  rdnsDone(id) { return this._post(`/api/evm/admin/rdns/${id}/done`, {}); }
  rdnsReject(id, reason) { return this._post(`/api/evm/admin/rdns/${id}/reject`, { reason }); }
  getVmRdns(id) { return this._get(`/api/evm/admin/vms/${id}/rdns`); }
  getSnapshotSchedule(id) { return this._get(`/api/evm/admin/vms/${id}/snapshot-schedule`); }
  saveSnapshotSchedule(id, data) { return this._post(`/api/evm/admin/vms/${id}/snapshot-schedule`, data); }
  setVmRdns(id, data) { return this._post(`/api/evm/admin/vms/${id}/rdns`, data); }
  getVmBackups(id) { return this._get(`/api/evm/admin/vms/${id}/backups`); }
  restoreVmBackup(id, backupId) { return this._post(`/api/evm/admin/vms/${id}/backup/restore`, { backup_id: backupId }); }
  deleteVmBackup(id, backupId) { return this._post(`/api/evm/admin/vms/${id}/backup/delete`, { backup_id: backupId }); }
  // scheduled encrypted backups (restic) — admin routes only
  getBackupPlan(id) { return this._get(`/api/evm/admin/backups/vms/${id}`); }
  runBackupNow(id) { return this._post(`/api/evm/admin/backups/vms/${id}/run`, {}); }
  restoreBackupPoint(id, pid, body = {}) { return this._post(`/api/evm/admin/backups/points/${pid}/restore`, { mode: body.dev ? 'disk' : 'same', dev: body.dev || undefined, power_on: body.power_on || 'keep' }); }
  deleteBackupPoint(id, pid) { return this._post(`/api/evm/admin/backups/points/${pid}/delete`, {}); }
  verifyBackupPoint(pid) { return this._post(`/api/evm/admin/backups/points/${pid}/verify`, {}); }
  getNodesOverview() { return this._get('/api/evm/admin/nodes/overview'); }
  getNodeMetrics(id, range = '24h') { return this._get(`/api/evm/admin/nodes/${id}/metrics`, { range }); }
  getNodeUplink(id, range = '24h') { return this._get(`/api/evm/admin/nodes/${id}/uplink`, { range }); }
  saveNodeUplink(id, alertMbps) { return this._post(`/api/evm/admin/nodes/${id}/uplink`, { alert_mbps: alertMbps }); }

  // ---- quotas / overage / manual restrictions ----
  getVmQuota(id) { return this._get(`/api/evm/admin/vms/${id}/quota`); }
  vmQuotaOp(id, op, data = {}) { return this._post(`/api/evm/admin/vms/${id}/quota`, { ...data, op }); }
  setVmLimit(id, data) { return this._post(`/api/evm/admin/vms/${id}/limit`, data); }
  liftVmLimit(id) { return this._post(`/api/evm/admin/vms/${id}/limit/lift`, {}); }
  getQuotaReport() { return this._get('/api/evm/admin/quota/report'); }
  getTrafficPackConfig(nodeId = 0) { return this._get('/api/evm/admin/quota/pack-config', { node_id: nodeId }); }
  saveTrafficPackConfig(data) { return this._post('/api/evm/admin/quota/pack-config', data); }

  // ---- overcommit (超售策略) ----
  getOvercommitReport() { return this._get('/api/evm/admin/overcommit/report'); }
  getNodeOvercommit(id) { return this._get(`/api/evm/admin/nodes/${id}/overcommit`); }
  saveNodeOvercommit(id, data) { return this._post(`/api/evm/admin/nodes/${id}/overcommit`, data); }

  // ---- VDS (独享物理核) ----
  getVdsNodes() { return this._get('/api/evm/admin/vds/nodes'); }
  getNodeVds(id) { return this._get(`/api/evm/admin/nodes/${id}/vds`); }
  saveNodeVds(id, data) { return this._post(`/api/evm/admin/nodes/${id}/vds`, data); }
  getVmVds(id) { return this._get(`/api/evm/admin/vms/${id}/vds`); }
  repinVmVds(id) { return this._post(`/api/evm/admin/vms/${id}/vds/repin`, {}); }
  pinVmVds(id, cpus) { return this._post(`/api/evm/admin/vms/${id}/vds/pin`, { cpus }); }

  // ---- paid orders waiting for capacity (待开通) ----
  getPendingProvisions(status = 'pending') { return this._get('/api/evm/admin/pending-provisions', { status }); }
  retryPendingProvision(id, nodeId) { return this._post(`/api/evm/admin/pending-provisions/${id}/retry`, nodeId ? { node_id: nodeId } : {}); }
  cancelPendingProvision(id) { return this._post(`/api/evm/admin/pending-provisions/${id}/cancel`, {}); }
  retryPendingElastic() { return this._post('/api/evm/admin/pending-provisions/elastic/retry', {}); }
  cancelPendingElastic(itemId) { return this._post(`/api/evm/admin/pending-provisions/elastic/${itemId}/cancel`, {}); }

  // ---- subscriptions (renewal / expiry) ----
  getSubscriptions(params) { return this._get('/api/evm/admin/subscriptions', params); }
  getRenewalReport() { return this._get('/api/evm/admin/subscriptions/report'); }
  renewSubscription(id, months, pay) { return this._post(`/api/evm/admin/subscriptions/${id}/renew`, { months, pay: pay ? 1 : 0 }); }
  extendSubscription(id, days) { return this._post(`/api/evm/admin/subscriptions/${id}/extend`, { days }); }
  suspendSubscription(id) { return this._post(`/api/evm/admin/subscriptions/${id}/suspend`); }
  unsuspendSubscription(id) { return this._post(`/api/evm/admin/subscriptions/${id}/unsuspend`); }
  setSubscriptionAutoRenew(id, enabled) { return this._post(`/api/evm/admin/subscriptions/${id}/auto-renew`, { enabled: enabled ? 1 : 0 }); }

  // Storage pools (admin)
  async getStoragePools(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/evm/storage-pool', { params });
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }
  createStoragePool(data) { return this._post('/api/admin/evm/storage-pool', data); }
  updateStoragePool(id, data) { return this._post(`/api/admin/evm/storage-pool/${id}`, data); }
  deleteStoragePool(id) { return this._post(`/api/admin/evm/storage-pool/${id}/delete`); }
  setNodeDiskRatio(nodeId, ratio) { return this._post(`/api/admin/evm/storage-pool/node/${nodeId}/ratio`, { ratio }); }

  // Ceph clusters (admin)
  getCephClusters() { return this._get('/api/admin/evm/ceph-cluster'); }
  createCephCluster(data) { return this._post('/api/admin/evm/ceph-cluster', data); }
  updateCephCluster(id, data) { return this._post(`/api/admin/evm/ceph-cluster/${id}`, data); }
  deleteCephCluster(id) { return this._post(`/api/admin/evm/ceph-cluster/${id}/delete`); }
  attachCephCluster(id, nodeId) { return this._post(`/api/admin/evm/ceph-cluster/${id}/attach`, { node_id: nodeId }); }
  detachCephCluster(id, nodeId) { return this._post(`/api/admin/evm/ceph-cluster/${id}/detach`, { node_id: nodeId }); }

  // Live / cold migration
  getMigratePreflight(id, mode) { return this._get(`/api/evm/admin/vms/${id}/migrate/preflight`, { mode }); }
  getMigrateStatus(id) { return this._get(`/api/evm/admin/vms/${id}/migrate/status`); }
  startMigrate(id, data) { return this._post(`/api/evm/admin/vms/${id}/migrate`, data); }
  cancelMigrate(id) { return this._post(`/api/evm/admin/vms/${id}/migrate/cancel`); }

  // Node failure / HA
  getNodeHa(id, params) { return this._get(`/api/admin/evm/node/${id}/ha`, params); }
  declareNodeDead(id, data) { return this._post(`/api/admin/evm/node/${id}/ha/declare-dead`, data); }
  clearNodeHa(id) { return this._post(`/api/admin/evm/node/${id}/ha/clear`); }

  // SSH keys bound to a VM (fingerprints only)
  getVmSshKeys(id) { return this._get(`/api/evm/admin/vms/${id}/ssh-keys`); }
  clearVmSshKeys(id) { return this._post(`/api/evm/admin/vms/${id}/ssh-keys/clear`, {}); }
  setVmSshKeyOnly(id, enabled) { return this._post(`/api/evm/admin/vms/${id}/ssh-keys/key-only`, { enabled: !!enabled }); }
}

export default new EvmAdminService();
